<script lang="ts">
	import type { Snippet } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import { preventDefaultAndPropagation } from '$lib/utils/preventDefaultAndPropagation';
	import maplibregl, { type LngLatLike, type MarkerOptions } from 'maplibre-gl';
	import { getMapContext, updatedMarkerContext } from 'svelte-maplibre';
	import { on } from 'svelte/events';
	// This is a rewrite of the svelte maplibre marker component available here
	// https://github.com/dimfeld/svelte-maplibre/blob/master/src/lib/Marker.svelte

	// The reason behind the rewrite is to have better control over the underlying maplibre marker
	// object. The svelte maplibre marker does not allow us to customize initialization options
	// such as the anchor of the marker.

	// By default svelte map libre marker uses the center of the marker as the anchor.

	// The svelte maplibre marker does not handle the on click event the way we want it to.
	// We need access to the native event object.

	interface Props {
		lngLat: LngLatLike;
		ariaLabel: string;
		dataTestId?: string;
		class?: ClassValue;
		markerOptions?: MarkerOptions;
		children?: Snippet;
		onclick?: () => void;
	}

	const {
		lngLat,
		ariaLabel,
		dataTestId,
		class: classes,
		markerOptions = {},
		children,
		onclick,
	}: Props = $props();

	const { map } = $derived(getMapContext());
	const { marker } = updatedMarkerContext();

	function addMarker(node: HTMLButtonElement) {
		if (!map) {
			return;
		}

		marker.value = new maplibregl.Marker({ element: node, ...markerOptions })
			.setLngLat(lngLat)
			.addTo(map);

		const off = on(node, 'click', (e) => {
			// 1/ Stop propagation of the click event to the map so that the map click event
			// does not fire on a marker click
			// 2/ Strange behavior where onclick on button element doesn't work, instead we use on from svelte/events,
			// more details: https://svelte.dev/docs/svelte/basic-markup#Events
			e.stopPropagation();
			onclick?.();
		});

		return {
			destroy() {
				off();
				marker?.value?.remove();
			},
		};
	}

	$effect.pre(() => {
		marker?.value?.setLngLat(lngLat);
	});
</script>

<button
	use:addMarker
	class={['p-0 b-0 cursor-pointer', classes]}
	aria-label={ariaLabel}
	data-testid={dataTestId}
	onpointerdown={preventDefaultAndPropagation}
	onmousedown={preventDefaultAndPropagation}
>
	{@render children?.()}
</button>
